import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AlertColor } from '@mui/material';
import { ActivityOrigin } from '@api/ActivityLogs/ActivityOrigin';

export interface ConfirmDialogType {
    open: boolean;
    title: string;
    message: string;
    buttons?: Array<{
        text: string;
        onPress: () => void;
        style?: 'default' | 'cancel' | 'destructive';
    }>;
    hideCloseButton?: boolean;
}

export interface AlertType {
    message: string;
    severity: AlertColor;
    open: boolean;
}

export interface ActivityLogDrawer {
    isOpen: boolean;
    section?: string;
    sectionId?: string;
    /**
     * Los renglones DERIVADOS del propio objeto (D8 del plan de la bitácora): su
     * creación y, en un pedido, los hitos que ocurrieron fuera del admin —lo
     * cobró Stripe, lo entregó Uber—. Los arma la pantalla con lo que ya tiene
     * en memoria: **no cuestan una lectura ni una escritura**, y por eso no
     * viajan como `ActivityLog`. El drawer los intercala por fecha con los
     * eventos registrados y los distingue en gris.
     *
     * Vacío o ausente = el objeto no sabe nada de sí mismo (hoy, todo el catálogo).
     */
    derived?: ActivityOrigin[];
}

export interface AppState {
    isLoading: boolean;
    loaderMessage: string;
    progressBar?: number;
    alert: AlertType;
    confirmDialog: ConfirmDialogType;
    darkMode: boolean;
    activityLogDrawer: ActivityLogDrawer;
}

const initialState: AppState = {
    alert: {
        message: 'Krispis',
        open: false,
        severity: 'success'
    },
    isLoading: false,
    loaderMessage: 'Cargando',
    confirmDialog: {
        open: false,
        title: '',
        message: '',
        buttons: [],
        hideCloseButton: false
    },
    darkMode: false,
    activityLogDrawer: {
        isOpen: false
    }
};

const appSlice = createSlice({
    name: 'app',
    initialState,
    reducers: {
        setIsLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
            state.loaderMessage = initialState.loaderMessage;
        },
        setLoaderMessage: (state, action: PayloadAction<string>) => {
            state.loaderMessage = action.payload;
        },
        setProgressBar: (state, action: PayloadAction<number | undefined>) => {
            state.progressBar = action.payload;
        },
        setAlert: (state, action: PayloadAction<AlertType>) => {
            state.alert = action.payload;
        },
        setActivityDrawer: (state, action: PayloadAction<ActivityLogDrawer>) => {
            state.activityLogDrawer = action.payload;
        },
        setConfirmDialog: (state, action: PayloadAction<ConfirmDialogType>) => {
            state.confirmDialog = action.payload;
        },
        closeConfirmDialog: (state) => {
            state.confirmDialog.open = false;
        },
        setDarkMode: (state, action: PayloadAction<boolean>) => {
            state.darkMode = action.payload;
        }
    }
});

export const {
    setIsLoading,
    setLoaderMessage,
    setProgressBar,
    setAlert,
    setConfirmDialog,
    closeConfirmDialog,
    setActivityDrawer,
    setDarkMode
} = appSlice.actions;

export default appSlice.reducer;
